/**
 * List-field values in either shape (ORCH-354)
 *
 * Purpose: A list field's value is stored in the shape its field declares (one UUID for
 *          single-select, a UUID array for multi-select), but a field's multiselect
 *          setting can flip without a migration, so stored rows may hold either shape.
 *          Read list values only through `asList` (the read side of
 *          `shared_core/services/list_values.py`). Forms submit what they hold; the server
 *          stores the declared shape.
 */

/** A stored list value as an array of strings: null/'' -> [], scalar -> [scalar]. */
export const asList = (value: unknown): string[] => {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== null && item !== undefined && item !== '')
      .map((item) => String(item));
  }
  return [String(value)];
};

/** The validation message for a single-select field holding several values. */
export const SINGLE_SELECT_ERROR = 'Choose one value';

/**
 * The reserved "every value, now and in future" list value (ORCH-418), stored as
 * `[ALL_VALUE]` on a multi-select. Mirrors `ALL_VALUE` in `list_values.py`; it is never a
 * MasterDataValue, so code that compares values goes through `isAll`.
 */
export const ALL_VALUE = '00000000-0000-4000-a000-0000000000a1';

/** The label ALL shows as, wherever a list value is displayed. */
export const ALL_LABEL = 'All';

/** Whether a stored list value (either shape) is ALL. */
export const isAll = (value: unknown): boolean => asList(value).includes(ALL_VALUE);

/** Whether a stored list value covers `id`: ALL covers every value (ORCH-418 §5.8). */
export const listIncludes = (value: unknown, id: string): boolean => {
  const ids = asList(value);
  return ids.includes(ALL_VALUE) || ids.includes(id);
};
